// Frontend mirror of backend/src/utils/transportCostAllocation.js — keep the two in sync.
//
// One key per truck (owner decisions 2026-09-30 / 2026-10-01,
// docs/decisions/2026-09-30-bez-palet-podzial-transportu-po-wartosci.md):
// szt. (default), m², m³, goods value or manual. The same key splits the
// freight between the loads on the truck and between the lines. Pallets,
// packages and LDM were removed.
//
// BY_SQM / BY_CBM per-line metrics come from the shared dimensionConversion
// helpers (the single home for the piece → m²/m³ geometry). The commercial
// unit is honoured exactly like the backend twin: a quantity already in the
// metric's unit IS the metric; a mismatched unit derives through the product
// dimensions and yields null (→ MISSING_DIMENSIONS) when they are absent.
//
// BY_VALUE weights are goods values (quantity × net price, PLN) that ONLY the
// server knows: OPERATIONS has no prices, so the frontend never computes a
// value split. A value passed here (`allocationValue`) is accepted for parity
// with the backend, but no screen supplies one — value shares are read from
// the saved transport (`costAllocation`, `routeOrders[].allocationPercent`).
import {
  calculateAreaM2,
  calculateVolumeM3,
  convertAllocationQuantity,
  isActiveQuantityUnit,
  normalizeQuantityUnit,
  resolveDimensionGeometryMm,
} from './dimensionConversion';
import { formatUnit } from './units';
import { roundMoney as roundMoneyHalfUp } from './money';
export { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from './polishNumberInput';

// F1 (trailer weight indicator) — SINGLE HOME, byte-identical to the backend
// twin: 22 800 kg max payload of a standard trailer. Display-only badge,
// never a save-blocking limit.
export const TRAILER_PAYLOAD_KG = 22800;

export const TRANSPORT_COST_ALLOCATION_METHODS = [
  'BY_COMMERCIAL_QTY',
  'BY_SQM',
  'BY_CBM',
  'BY_VALUE',
  'MANUAL',
] as const;

export type AllocationMethod = typeof TRANSPORT_COST_ALLOCATION_METHODS[number];

// Ordered option list of the one "Klucz podziału kosztu transportu" picker
// (szt. / m² / m³ / wartość towaru / ręcznie), shared by the form and the
// detail page.
export const TRANSPORT_COST_ALLOCATION_METHOD_ORDER: readonly AllocationMethod[] = [
  'BY_COMMERCIAL_QTY',
  'BY_SQM',
  'BY_CBM',
  'BY_VALUE',
  'MANUAL',
];

export const DEFAULT_ALLOCATION_METHOD: AllocationMethod = 'BY_COMMERCIAL_QTY';

// Between-loads mirror written by the server to
// TransportOrderAllocation."allocationMethod" (never sent by the form).
export type BetweenLoadsMethod = 'QUANTITY' | 'SQM' | 'CBM' | 'VALUE' | 'MANUAL';
export const BETWEEN_LOADS_METHOD: Readonly<Record<AllocationMethod, BetweenLoadsMethod>> = {
  BY_COMMERCIAL_QTY: 'QUANTITY',
  BY_SQM: 'SQM',
  BY_CBM: 'CBM',
  BY_VALUE: 'VALUE',
  MANUAL: 'MANUAL',
};

/** A known key, or null for an unknown / retired one (BY_LDM). */
export function normalizeAllocationKey(method: unknown): AllocationMethod | null {
  if (method === null || method === undefined || method === '') return null;
  const normalized = String(method).toUpperCase();
  return (TRANSPORT_COST_ALLOCATION_METHODS as readonly string[]).includes(normalized)
    ? normalized as AllocationMethod
    : null;
}

/** The truck key behind a saved between-loads mirror (QUANTITY → szt.). */
export function allocationKeyFromBetweenLoads(method: unknown): AllocationMethod | null {
  const normalized = String(method || '').toUpperCase();
  const entry = (Object.entries(BETWEEN_LOADS_METHOD) as Array<[AllocationMethod, BetweenLoadsMethod]>)
    .find(([, between]) => between === normalized);
  return entry ? entry[0] : null;
}

// Persistence bound for the money fields backing this calculation
// (NUMERIC(14,2) in migration 091): 12 integer digits + 2 decimals.
export const MAX_MONEY_AMOUNT = 999999999999.99;

export type TransportCostAllocationWarningCode =
  | 'ZERO_TOTAL_QUANTITY'
  | 'MIXED_UNITS'
  | 'ZERO_TOTAL_METRIC'
  | 'MISSING_DIMENSIONS'
  | 'MISSING_VALUE'
  | 'COUNTED_IN_PIECES_ONLY'
  | 'MANUAL_SUM_MISMATCH'
  | 'NEGATIVE_MANUAL_ALLOCATION'
  | 'NON_POSITIVE_QUANTITY';

export interface TransportCostAllocationInputItem {
  id?: string;
  quantity?: number | string | null;
  unit?: string | null;
  goodsValueTotal?: number | string | null;
  manualAllocatedTransportCost?: number | string | null;
  // Product identity + dimensions (mm) used by BY_SQM / BY_CBM.
  name?: string | null;
  productName?: string | null;
  width?: number | string | null;
  length?: number | string | null;
  height?: number | string | null;
  thickness?: number | string | null;
  /** BY_VALUE weight (PLN). Server-side only — no screen passes it. */
  allocationValue?: number | string | null;
  /** „Liczymy tylko w sztukach”: no m² / m³ — 0 under m² / m³ + warning. */
  countInPiecesOnly?: boolean | null;
  /** Pieces of the loaded part (quantity set) — the szt. weight over mixed units. */
  quantityPcs?: number | string | null;
  pcs?: number | string | null;
}

export interface TransportCostAllocationInput {
  transportCost?: number | string | null;
  method?: AllocationMethod | string | null;
  items?: TransportCostAllocationInputItem[];
}

export interface TransportCostAllocationWarningItemRef {
  itemId?: string;
  name?: string | null;
}

export interface TransportCostAllocationWarning {
  code: TransportCostAllocationWarningCode;
  itemId?: string;
  // MISSING_DIMENSIONS / MISSING_VALUE / NEGATIVE_MANUAL_ALLOCATION: the
  // offending lines (id + product name), one aggregated warning.
  items?: TransportCostAllocationWarningItemRef[];
}

// Single home for mapping warning codes to transport.json i18n keys — used by
// the form and the detail page.
export function commercialAllocationWarningKey(code: TransportCostAllocationWarningCode): string {
  switch (code) {
    case 'ZERO_TOTAL_QUANTITY':
      return 'commercialAllocationWarningZeroTotalQuantity';
    case 'MIXED_UNITS':
      return 'commercialAllocationWarningMixedUnits';
    case 'ZERO_TOTAL_METRIC':
      return 'commercialAllocationWarningZeroTotalMetric';
    case 'MISSING_DIMENSIONS':
      return 'commercialAllocationWarningMissingDimensions';
    case 'MISSING_VALUE':
      return 'commercialAllocationWarningMissingValue';
    case 'COUNTED_IN_PIECES_ONLY':
      return 'commercialAllocationWarningCountedInPiecesOnly';
    case 'MANUAL_SUM_MISMATCH':
      return 'commercialAllocationWarningManualSumMismatch';
    case 'NEGATIVE_MANUAL_ALLOCATION':
      return 'commercialAllocationWarningNegativeManualAllocation';
    case 'NON_POSITIVE_QUANTITY':
    default:
      return 'commercialAllocationWarningNonPositiveQuantity';
  }
}

// Single home for the label i18n key of each key option.
export function commercialAllocationMethodLabelKey(method: AllocationMethod): string {
  switch (method) {
    case 'BY_SQM':
      return 'commercialAllocationBySqm';
    case 'BY_CBM':
      return 'commercialAllocationByCbm';
    case 'BY_VALUE':
      return 'commercialAllocationByValue';
    case 'MANUAL':
      return 'commercialAllocationManual';
    case 'BY_COMMERCIAL_QTY':
    default:
      return 'commercialAllocationByCommercialQty';
  }
}

// Per-option explanation i18n key (rulebook definitions).
export function commercialAllocationMethodTooltipKey(method: AllocationMethod): string | null {
  switch (method) {
    case 'BY_SQM':
      return 'commercialAllocationBySqmTooltip';
    case 'BY_CBM':
      return 'commercialAllocationByCbmTooltip';
    case 'BY_VALUE':
      return 'commercialAllocationByValueTooltip';
    default:
      return null;
  }
}

export interface CostAllocationMethodOptionDef {
  value: AllocationMethod;
  labelKey: string;
  tooltipKey: string | null;
}

// Ordered option definitions (value + i18n keys) for the key picker.
export function buildCostAllocationMethodOptionDefs(): CostAllocationMethodOptionDef[] {
  return TRANSPORT_COST_ALLOCATION_METHOD_ORDER.map((method) => ({
    value: method,
    labelKey: commercialAllocationMethodLabelKey(method),
    tooltipKey: commercialAllocationMethodTooltipKey(method),
  }));
}

// ---------------------------------------------------------------------------
// Key availability — mirror of resolveAllocationKeyAvailability (backend single
// home). Informational only: it greys out picker options; neither side ever
// rejects a key. The create form uses this mirror (without prices); the edit
// form reads the server's `allocationKeyAvailability`.
// ---------------------------------------------------------------------------
export interface AllocationKeyReason {
  productName: string | null;
  reason: string;
}

export interface AllocationKeyState {
  available: boolean;
  reasons: AllocationKeyReason[];
}

export type AllocationKeyAvailability = Record<AllocationMethod, AllocationKeyState>;

export interface AllocationKeyPosition {
  productName?: string | null;
  name?: string | null;
  unit?: string | null;
  quantity?: number | string | null;
  /** m² / m³ of the loaded part (quantity set), when stored. */
  m2?: number | string | null;
  m3?: number | string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  source: 'ZK' | 'ZD';
  /** ZK: the position is a cargo line (manual amounts live there). */
  cargoRow?: boolean;
  /** „Liczymy tylko w sztukach” of the product (migration 196). */
  countInPiecesOnly?: boolean | null;
}

// Same texts as the backend twin.
export const COUNTED_IN_PIECES_ONLY_REASON = 'liczymy tylko w sztukach';
// One reason for the whole truck.
export const MANUAL_ON_STOCK_REASON = 'koszt trafia do partii z zamówień do dostawcy — podziału ręcznego nie da się na nie przenieść';

function positiveNumber(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

type MetricSource = {
  quantity?: number | string | null;
  unit?: string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  countInPiecesOnly?: boolean | null;
};

function geometryOf(source: MetricSource) {
  return resolveDimensionGeometryMm({
    width: toDimensionMm(source.width),
    length: toDimensionMm(source.length),
    height: toDimensionMm(source.height),
    thickness: toDimensionMm(source.thickness),
  });
}

/**
 * m² of a loaded position derived from its quantity and dimensions — mirror of
 * backend utils/transportMetrics.calculateProductAreaSqm (0 = cannot derive).
 */
export function calculateProductAreaSqm(product: MetricSource): number {
  // „Liczymy tylko w sztukach”: no m² on a truck at all (not even an m² line).
  if (product.countInPiecesOnly === true) return 0;
  const quantity = positiveNumber(product.quantity);
  const unit = normalizeQuantityUnit(product.unit == null ? 'pcs' : String(product.unit));
  if (quantity <= 0 || !isActiveQuantityUnit(unit)) return 0;
  if (unit === 'm2') return quantity;
  const { areaPerPieceM2, depthMm } = geometryOf(product);
  if (unit === 'm3') return depthMm ? quantity / (depthMm / 1000) : 0;
  return areaPerPieceM2 ? areaPerPieceM2 * quantity : 0;
}

/** m³ twin of calculateProductAreaSqm (backend transportMetrics). */
export function calculateProductVolumeCbm(product: MetricSource): number {
  if (product.countInPiecesOnly === true) return 0;
  const quantity = positiveNumber(product.quantity);
  const unit = normalizeQuantityUnit(product.unit == null ? 'pcs' : String(product.unit));
  if (quantity <= 0 || !isActiveQuantityUnit(unit)) return 0;
  if (unit === 'm3') return quantity;
  const { areaPerPieceM2, depthMm } = geometryOf(product);
  if (!depthMm) return 0;
  if (unit === 'm2') return quantity * (depthMm / 1000);
  return areaPerPieceM2 ? areaPerPieceM2 * (depthMm / 1000) * quantity : 0;
}

/**
 * Pieces of a loaded position — mirror of backend
 * transportMetrics.calculateProductPieces. The catalog weight is per piece, so
 * an m² / m³ line weighs its persisted piece count, else the count derived
 * from the dimensions (dimensionConversion); null when neither gives one.
 */
export function calculateProductPieces(product: MetricSource & { quantityPcs?: number | string | null }): number | null {
  const quantity = positiveNumber(product.quantity);
  const unit = normalizeQuantityUnit(product.unit == null ? 'pcs' : String(product.unit));
  if (quantity <= 0) return 0;
  if (unit === 'pcs') return quantity;
  const persisted = positiveNumber(product.quantityPcs);
  if (persisted > 0) return persisted;
  const { exactValue } = convertAllocationQuantity({
    quantity,
    sourceUnit: unit,
    targetUnit: 'pcs',
    width: toDimensionMm(product.width),
    length: toDimensionMm(product.length),
    height: toDimensionMm(product.height),
    thickness: toDimensionMm(product.thickness),
    countInPiecesOnly: product.countInPiecesOnly === true,
  });
  return exactValue && exactValue > 0 ? exactValue : null;
}

// m² / m³ of one position: entered in its quantity set, else derived from the
// dimensions — the figure the between-loads basis weighs (backend twin).
export function positionArea(position: AllocationKeyPosition): number {
  if (position?.countInPiecesOnly === true) return 0;
  return positiveNumber(position.m2) || calculateProductAreaSqm(position);
}

export function positionVolume(position: AllocationKeyPosition): number {
  if (position?.countInPiecesOnly === true) return 0;
  return positiveNumber(position.m3) || calculateProductVolumeCbm(position);
}

/**
 * szt. key over different units (backend single home positionPieces): every
 * position weighs its pieces from the persisted quantity set (`pcs` /
 * `quantityPcs`; a line in szt. without a set weighs its quantity), never a
 * sum of unlike units; a position without pieces weighs 0.
 */
export function positionPieces(position: {
  pcs?: number | string | null;
  quantityPcs?: number | string | null;
  unit?: string | null;
  quantity?: number | string | null;
} | null | undefined): number {
  const pcs = positiveNumber(position?.pcs ?? position?.quantityPcs);
  if (pcs > 0) return pcs;
  return normalizeQuantityUnit(position?.unit == null ? 'pcs' : String(position.unit)) === 'pcs'
    ? positiveNumber(position?.quantity)
    : 0;
}

/** True when the loaded positions (quantity > 0) are in more than one unit. */
export function hasMixedUnits(positions: Array<{ unit?: string | null; quantity?: number | string | null } | null | undefined> = []): boolean {
  const units = new Set((positions || [])
    .filter((position) => position && Number(position.quantity) > 0)
    .map((position) => normalizeQuantityUnit(position?.unit == null ? 'pcs' : String(position.unit)))
    .filter(Boolean));
  return units.size > 1;
}

function pushReason(reasons: AllocationKeyReason[], productName: string | null, reason: string) {
  if (!reasons.some((entry) => entry.productName === productName && entry.reason === reason)) {
    reasons.push({ productName, reason });
  }
}

export function resolveAllocationKeyAvailability(
  positions: AllocationKeyPosition[] = [],
): AllocationKeyAvailability {
  const loaded = (positions || []).filter((position) => position && Number(position.quantity ?? 0) > 0);
  const reasons = Object.fromEntries(
    TRANSPORT_COST_ALLOCATION_METHODS.map((key) => [key, [] as AllocationKeyReason[]]),
  ) as Record<AllocationMethod, AllocationKeyReason[]>;
  for (const position of loaded) {
    const productName = position.productName || position.name || null;
    const unit = normalizeQuantityUnit(position.unit == null ? 'pcs' : String(position.unit));
    if (unit !== 'pcs') pushReason(reasons.BY_COMMERCIAL_QTY, productName, `jednostka ${formatUnit(position.unit)}`);
    if (position.countInPiecesOnly === true) {
      pushReason(reasons.BY_SQM, productName, COUNTED_IN_PIECES_ONLY_REASON);
      pushReason(reasons.BY_CBM, productName, COUNTED_IN_PIECES_ONLY_REASON);
    } else {
      const area = positionArea(position);
      if (!(area > 0)) {
        pushReason(reasons.BY_SQM, productName, unit === 'm3' ? 'jednostka m³ bez wymiarów' : 'brak wymiarów');
      }
      if (!(positionVolume(position) > 0)) {
        pushReason(reasons.BY_CBM, productName, area > 0 ? 'brak grubości' : 'brak wymiarów');
      }
    }
    if (position.source === 'ZD') {
      pushReason(reasons.MANUAL, null, MANUAL_ON_STOCK_REASON);
    } else if (position.cargoRow === false) {
      pushReason(reasons.MANUAL, productName, 'brak pozycji ładunku z kwotą');
    }
  }
  return Object.fromEntries(TRANSPORT_COST_ALLOCATION_METHODS.map((key) => [
    key,
    { available: reasons[key].length === 0, reasons: reasons[key] },
  ])) as AllocationKeyAvailability;
}

/** "Okno PCV — brak wymiarów; Płyty — jednostka m³" (or the bare reason). */
export function formatAllocationKeyReasons(reasons: AllocationKeyReason[] = []): string {
  return reasons
    .map((entry) => (entry.productName ? `${entry.productName} — ${entry.reason}` : entry.reason))
    .join('; ');
}

export interface TransportCostAllocationItemResult {
  id?: string;
  quantity: number;
  unit: string | null;
  goodsValueTotal: number;
  manualAllocatedTransportCost: number;
  /**
   * Server panel (GET /transport-routes/:id): the booked ZK share split over
   * that ZK's lines; `null` = the key cannot weigh the line ("—", never 0).
   * The local mirror always returns a number.
   */
  allocatedTransportCost: number | null;
  /** Share of the line in the truck's freight, 4 dp (null = cannot say). */
  allocationSharePercent: number | null;
  goodsCostPerUnit: number | null;
  transportCostPerUnit: number | null;
  landedCostTotal: number | null;
  landedCostPerUnit: number | null;
  // Per-metric unit costs; null when the metric cannot be computed — render "—".
  transportCostPerAreaSqm: number | null;
  transportCostPerVolumeM3: number | null;
}

export interface TransportCostAllocationSummary {
  totalGoodsValue: number;
  totalAllocatedTransportCost: number;
  totalLandedCost: number;
  /**
   * Rest of the truck after the lines. The local mirror always returns a
   * number; the server panel returns `null` when the truck in PLN is not known.
   */
  allocationDifference: number | null;
}

export interface TransportCostAllocationResult {
  method: AllocationMethod;
  items: TransportCostAllocationItemResult[];
  summary: TransportCostAllocationSummary;
  warnings: TransportCostAllocationWarning[];
}

interface NormalizedAllocationItem {
  id?: string;
  name: string | null;
  quantity: number;
  unit: string | null;
  pieces: number;
  goodsValueTotal: number;
  manualAllocatedTransportCost: number;
  manualAllocationInvalid: boolean;
  allocationValue: number | null;
  dimensionSource: {
    width: number | null;
    length: number | null;
    height: number | null;
    thickness: number | null;
    unit: string | null;
    countInPiecesOnly: boolean;
  };
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

// Exported so the goods-value prefill rounds ties (1.005 -> 1.01) identically
// to the allocation math.
// The one half-up of money (money.ts roundMoney); a missing value counts as 0.
export function roundMoney(value: unknown): number {
  return roundMoneyHalfUp(toNumber(value));
}

function normalizeMethod(method: TransportCostAllocationInput['method']): AllocationMethod {
  return normalizeAllocationKey(method) || DEFAULT_ALLOCATION_METHOD;
}

// A dimension counts only when it is finite AND strictly positive.
function toDimensionMm(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function normalizeItem(item: TransportCostAllocationInputItem = {}): NormalizedAllocationItem {
  const preferredName = item.productName || item.name || null;
  // A negative manual allocation is nulled to 0 and flagged (allocations are
  // never negative); the caller raises NEGATIVE_MANUAL_ALLOCATION.
  const rawManual = roundMoney(item.manualAllocatedTransportCost);
  const manualAllocationInvalid = rawManual < 0;
  return {
    id: item.id,
    name: preferredName != null && String(preferredName).trim() !== '' ? String(preferredName) : null,
    quantity: toNumber(item.quantity),
    unit: item.unit || null,
    pieces: positionPieces(item),
    goodsValueTotal: roundMoney(item.goodsValueTotal),
    manualAllocatedTransportCost: manualAllocationInvalid ? 0 : rawManual,
    manualAllocationInvalid,
    allocationValue: toDimensionMm(item.allocationValue),
    dimensionSource: {
      width: toDimensionMm(item.width),
      length: toDimensionMm(item.length),
      height: toDimensionMm(item.height),
      thickness: toDimensionMm(item.thickness),
      unit: item.unit || null,
      countInPiecesOnly: item.countInPiecesOnly === true,
    },
  };
}

// Split transportCost across sourceValues by their non-negative weights
// (cumulative rounding: every share >= 0, the shares sum to the rounded
// cost exactly). Mirrors the backend twin's allocateProportionally exactly.
export function allocateProportionally(transportCost: number, sourceValues: number[]): number[] {
  const roundedTransportCost = roundMoney(transportCost);
  const weights = sourceValues.map((value) => Math.max(toNumber(value), 0));
  const total = weights.reduce((sum, value) => sum + value, 0);

  if (total <= 0) {
    return sourceValues.map(() => 0);
  }

  let cumulativeWeight = 0;
  let previousRoundedCost = 0;

  return weights.map((weight) => {
    if (weight <= 0) {
      return 0;
    }

    cumulativeWeight += weight;
    const cumulativeCost = roundMoney((cumulativeWeight / total) * roundedTransportCost);
    const allocation = roundMoney(cumulativeCost - previousRoundedCost);
    previousRoundedCost = cumulativeCost;
    return allocation;
  });
}

function calculateAllocationMetric(item: NormalizedAllocationItem, method: AllocationMethod): number | null {
  if (method === 'BY_SQM') {
    return calculateAreaM2(item.dimensionSource, item.quantity);
  }

  if (method === 'BY_CBM') {
    return calculateVolumeM3(item.dimensionSource, item.quantity);
  }

  if (method === 'BY_VALUE') {
    return item.allocationValue;
  }

  return item.quantity;
}

function buildItemListWarning(
  code: TransportCostAllocationWarningCode,
  items: NormalizedAllocationItem[],
): TransportCostAllocationWarning {
  return {
    code,
    items: items.map((item) => ({ itemId: item.id, name: item.name })),
  };
}

function buildMissingMetricWarning(
  items: NormalizedAllocationItem[],
  method: AllocationMethod,
): TransportCostAllocationWarning {
  return buildItemListWarning(method === 'BY_VALUE' ? 'MISSING_VALUE' : 'MISSING_DIMENSIONS', items);
}

function isWeightedAllocationMethod(method: AllocationMethod): boolean {
  return method === 'BY_SQM' || method === 'BY_CBM' || method === 'BY_VALUE';
}

function buildPerMetricUnitCosts(
  item: NormalizedAllocationItem,
  roundedAllocatedTransportCost: number,
): Pick<TransportCostAllocationItemResult, 'transportCostPerAreaSqm' | 'transportCostPerVolumeM3'> {
  const areaSqm = calculateAreaM2(item.dimensionSource, item.quantity);
  const volumeM3 = calculateVolumeM3(item.dimensionSource, item.quantity);

  return {
    transportCostPerAreaSqm: areaSqm && areaSqm > 0 ? roundMoney(roundedAllocatedTransportCost / areaSqm) : null,
    transportCostPerVolumeM3: volumeM3 && volumeM3 > 0 ? roundMoney(roundedAllocatedTransportCost / volumeM3) : null,
  };
}

function roundSharePercent(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  const rounded = Number(Number(value).toFixed(4));
  return rounded === 0 ? 0 : rounded;
}

function buildItemOutput(
  item: NormalizedAllocationItem,
  allocatedTransportCost: number,
  allocationSharePercent: number | null | undefined,
): TransportCostAllocationItemResult {
  const roundedAllocatedTransportCost = roundMoney(allocatedTransportCost);
  const landedCostTotal = roundMoney(item.goodsValueTotal + roundedAllocatedTransportCost);
  const hasPositiveQuantity = item.quantity > 0;

  return {
    id: item.id,
    quantity: item.quantity,
    unit: item.unit,
    goodsValueTotal: item.goodsValueTotal,
    manualAllocatedTransportCost: item.manualAllocatedTransportCost,
    allocatedTransportCost: roundedAllocatedTransportCost,
    allocationSharePercent: roundSharePercent(allocationSharePercent),
    goodsCostPerUnit: hasPositiveQuantity ? roundMoney(item.goodsValueTotal / item.quantity) : null,
    transportCostPerUnit: hasPositiveQuantity
      ? roundMoney(roundedAllocatedTransportCost / item.quantity)
      : null,
    landedCostTotal,
    landedCostPerUnit: hasPositiveQuantity ? roundMoney(landedCostTotal / item.quantity) : null,
    ...buildPerMetricUnitCosts(item, roundedAllocatedTransportCost),
  };
}

export function calculateTransportCostAllocation({
  transportCost,
  method,
  items,
}: TransportCostAllocationInput = {}): TransportCostAllocationResult {
  const normalizedMethod = normalizeMethod(method);
  const normalizedItems = Array.isArray(items) ? items.map(normalizeItem) : [];
  const roundedTransportCost = roundMoney(transportCost);
  const warnings: TransportCostAllocationWarning[] = [];

  normalizedItems.forEach((item) => {
    if (item.quantity <= 0) {
      warnings.push({ code: 'NON_POSITIVE_QUANTITY', itemId: item.id });
    }
  });

  let allocations: number[] = normalizedItems.map(() => 0);
  let shares: Array<number | null> = normalizedItems.map(() => 0);

  if (normalizedMethod === 'MANUAL') {
    const negativeManualItems = normalizedItems.filter((item) => item.manualAllocationInvalid);
    if (negativeManualItems.length > 0) {
      warnings.push(buildItemListWarning('NEGATIVE_MANUAL_ALLOCATION', negativeManualItems));
    }

    allocations = normalizedItems.map((item) => item.manualAllocatedTransportCost);
    shares = allocations.map((amount) => (
      roundedTransportCost > 0 ? (amount / roundedTransportCost) * 100 : null
    ));
  } else {
    // Quantity <= 0 means "not loaded on this transport": those items never
    // receive an automatic allocation, under any method.
    const eligibleIndexes = normalizedItems.reduce<number[]>((indexes, item, index) => {
      if (item.quantity > 0) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    if (eligibleIndexes.length === 0) {
      warnings.push({ code: 'ZERO_TOTAL_QUANTITY' });
    } else if (eligibleIndexes.length === 1) {
      if (isWeightedAllocationMethod(normalizedMethod)) {
        const metric = calculateAllocationMetric(normalizedItems[eligibleIndexes[0]], normalizedMethod);
        if (metric === null) {
          warnings.push(buildMissingMetricWarning([normalizedItems[eligibleIndexes[0]]], normalizedMethod));
        }
      }

      allocations[eligibleIndexes[0]] = roundedTransportCost;
      shares[eligibleIndexes[0]] = 100;
    } else if (isWeightedAllocationMethod(normalizedMethod)) {
      const missingMetricItems: NormalizedAllocationItem[] = [];
      const piecesOnlyItems: NormalizedAllocationItem[] = [];
      const sourceValues = normalizedItems.map((item) => {
        if (item.quantity <= 0) {
          return 0;
        }

        // „Liczymy tylko w sztukach”: no m² / m³ for this product → 0 and its
        // own warning (never MISSING_DIMENSIONS, nothing to fill in).
        if (item.dimensionSource.countInPiecesOnly
          && (normalizedMethod === 'BY_SQM' || normalizedMethod === 'BY_CBM')) {
          piecesOnlyItems.push(item);
          return 0;
        }

        const metric = calculateAllocationMetric(item, normalizedMethod);
        if (metric === null) {
          missingMetricItems.push(item);
          return 0;
        }

        return Math.max(toNumber(metric), 0);
      });
      const totalMetric = sourceValues.reduce((sum, value) => sum + value, 0);

      if (missingMetricItems.length > 0) {
        warnings.push(buildMissingMetricWarning(missingMetricItems, normalizedMethod));
      }
      if (piecesOnlyItems.length > 0) {
        warnings.push(buildItemListWarning('COUNTED_IN_PIECES_ONLY', piecesOnlyItems));
      }

      if (totalMetric <= 0) {
        warnings.push({ code: 'ZERO_TOTAL_METRIC' });
      } else {
        allocations = allocateProportionally(roundedTransportCost, sourceValues);
        shares = sourceValues.map((value) => (value / totalMetric) * 100);
      }
    } else {
      // Different units: pieces of each line (positionPieces), never a sum of
      // unlike units — the same weight the between-loads split uses.
      const mixedUnits = hasMixedUnits(eligibleIndexes.map((index) => normalizedItems[index]));
      if (mixedUnits) {
        warnings.push({ code: 'MIXED_UNITS' });
      }

      const sourceValues = normalizedItems.map((item) => {
        if (!(item.quantity > 0)) return 0;
        return mixedUnits ? item.pieces : item.quantity;
      });
      const totalQuantity = sourceValues.reduce((sum, value) => sum + value, 0);
      if (mixedUnits && totalQuantity <= 0) {
        warnings.push({ code: 'ZERO_TOTAL_METRIC' });
      }
      allocations = allocateProportionally(roundedTransportCost, sourceValues);
      shares = sourceValues.map((value) => (totalQuantity > 0 ? (value / totalQuantity) * 100 : 0));
    }
  }

  const outputItems = normalizedItems.map((item, index) => buildItemOutput(
    item,
    allocations[index] || 0,
    shares[index],
  ));
  const totalGoodsValue = roundMoney(outputItems.reduce((sum, item) => sum + item.goodsValueTotal, 0));
  const totalAllocatedTransportCost = roundMoney(
    outputItems.reduce((sum, item) => sum + (item.allocatedTransportCost ?? 0), 0),
  );
  const totalLandedCost = roundMoney(totalGoodsValue + totalAllocatedTransportCost);
  const allocationDifference = roundMoney(roundedTransportCost - totalAllocatedTransportCost);

  if (normalizedMethod === 'MANUAL' && Math.abs(allocationDifference) >= 0.01) {
    warnings.push({ code: 'MANUAL_SUM_MISMATCH' });
  }

  return {
    method: normalizedMethod,
    items: outputItems,
    summary: {
      totalGoodsValue,
      totalAllocatedTransportCost,
      totalLandedCost,
      allocationDifference,
    },
    warnings,
  };
}
